import React, { useState, useEffect } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import styled from 'styled-components';

const SettingsContainer = styled.div`
  padding: 0 5% 50px 5%;
  min-height: 100vh;
  background-color: #0b0614;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SettingsSection = styled.div`
    background: var(--glass-bg);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    padding: 25px;
    max-width: 800px;
    margin: 0 auto;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 20px;
    color: var(--primary-color);
    border-bottom: 2px solid var(--gradient-pink);
    padding-bottom: 10px;
`;

const SettingItem = styled.div`
    margin-bottom: 25px;
    
    label {
        display: block;
        margin-bottom: 8px;
        color: var(--primary-color);
        font-weight: 600;
    }
    
    input {
        width: 100%;
        padding: 12px;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid var(--border-color);
        border-radius: 8px;
        color: var(--primary-color);
        font-size: 1rem;
        transition: all 0.3s ease;
        
        &:focus {
            outline: none;
            border-color: var(--gradient-pink);
            background: rgba(255, 255, 255, 0.08);
        }
    }
    
    small {
        display: block;
        margin-top: 8px;
        color: var(--secondary-color);
        font-size: 0.85rem;
    }
`;

const SaveButton = styled.button`
    padding: 15px 40px;
    font-size: 1.1rem;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--gradient-pink), var(--gradient-cyan));
    color: white;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 20px;
    
    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 10px 30px rgba(255, 0, 127, 0.3);
    }
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const InfoBox = styled.div`
    background: rgba(0, 255, 255, 0.1);
    border: 1px solid var(--gradient-cyan);
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 20px;
    
    p {
        color: var(--primary-color);
        margin: 5px 0;
        line-height: 1.6;
    }
`;

const AdminTMDBPage = () => {
    const [settings, setSettings] = useState({
        tmdb_api_key: ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await fetch(getApiUrl(API_ENDPOINTS.GET_SETTINGS));
            if (response.ok) {
                const data = await response.json();
                setSettings({
                    tmdb_api_key: data.tmdb_api_key || ''
                });
            }
        } catch (error) {
            console.error('Ayarlar yüklenirken hata:', error);
        }
    };

    const handleSettingChange = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const saveSettings = async () => {
        setLoading(true);
        setMessage('');
        
        try {
            const response = await fetch(getApiUrl(API_ENDPOINTS.SAVE_SETTINGS), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(settings)
            });

            const result = await response.json();
            
            if (result.success) {
                setMessage('Ayarlar başarıyla kaydedildi!');
                setTimeout(() => setMessage(''), 3000);
            } else {
                setMessage('Hata: ' + (result.message || 'Ayarlar kaydedilemedi'));
            }
        } catch (error) {
            console.error('Ayarlar kaydedilirken hata:', error);
            setMessage('Ayarlar kaydedilirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    return (
        <SettingsContainer>
            <Header>
                <Title>TMDB Ayarları</Title>
            </Header>

            <SettingsSection>
                <SectionTitle>API Key Yönetimi</SectionTitle>
                
                <InfoBox>
                    <p><strong>⚠️ Güvenlik Uyarısı:</strong></p>
                    <p>TMDB API key'iniz güvenli bir şekilde saklanır ve sadece admin panelinde görüntülenir.</p>
                    <p>API key'inizi asla paylaşmayın ve güvenli tutun.</p>
                </InfoBox>
                
                <SettingItem>
                    <label>TMDB API Key</label>
                    <input
                        type="password"
                        value={settings.tmdb_api_key || ''}
                        onChange={(e) => handleSettingChange('tmdb_api_key', e.target.value)}
                        placeholder="Kendi TMDB API key'inizi girin"
                    />
                    <small>
                        <a href="https://www.themoviedb.org/settings/api" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gradient-cyan)' }}>
                            TMDB'den ücretsiz API key alabilirsiniz →
                        </a>
                    </small>
                </SettingItem>

                {message && (
                    <div style={{ 
                        padding: '15px', 
                        marginTop: '20px',
                        borderRadius: '8px',
                        background: message.includes('başarıyla') ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)',
                        color: message.includes('başarıyla') ? 'var(--gradient-cyan)' : 'var(--gradient-pink)',
                        border: `1px solid ${message.includes('başarıyla') ? 'var(--gradient-cyan)' : 'var(--gradient-pink)'}`
                    }}>
                        {message}
                    </div>
                )}

                <div style={{ textAlign: 'center', marginTop: '30px' }}>
                    <SaveButton onClick={saveSettings} disabled={loading}>
                        {loading ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                    </SaveButton>
                </div>
            </SettingsSection>
        </SettingsContainer>
    );
};

export default AdminTMDBPage;

